import { useCallback, useState } from "react";
import type { FieldValues } from "./engine/types";
import { SessionProvider } from "./ui/session";
import { AuthDialog } from "./ui/AuthDialog";
import { AppShell } from "./ui/AppShell";
import { Catalog } from "./ui/Catalog";
import { FieldScreen } from "./ui/FieldScreen";
import { HistoryScreen } from "./ui/HistoryScreen";
import { CoverageScreen } from "./ui/CoverageScreen";
import { EmptyState, Button } from "./ui/primitives";
import { useRouter, type Route } from "./ui/router";

export function App() {
  return (
    <SessionProvider>
      <Routes />
      <AuthDialog />
    </SessionProvider>
  );
}

function Routes() {
  const { route, navigate } = useRouter();

  /**
   * Re-run hand-off: History holds the stored answers, FieldScreen holds the
   * form. This carries one across the navigation so "عدّل وأعِد" lands on a
   * pre-filled form instead of a blank one.
   */
  const [rerun, setRerun] = useState<{ fieldId: string; values: FieldValues } | null>(null);

  const onRerun = useCallback(
    (fieldId: string, values: FieldValues) => {
      setRerun({ fieldId, values });
      navigate({ name: "field", fieldId });
    },
    [navigate],
  );

  const go = useCallback(
    (r: Route) => {
      // Leaving the field screen drops a pending prefill so a later plain visit
      // isn't haunted by an old run's answers.
      if (r.name !== "field") setRerun(null);
      navigate(r);
    },
    [navigate],
  );

  return (
    <AppShell route={route} navigate={go}>
      {route.name === "catalog" ? <Catalog navigate={go} /> : null}

      {route.name === "field" ? (
        <FieldScreen
          key={route.fieldId}
          fieldId={route.fieldId}
          navigate={go}
          initialValues={rerun?.fieldId === route.fieldId ? rerun.values : undefined}
        />
      ) : null}

      {route.name === "history" ? <HistoryScreen navigate={go} onRerun={onRerun} /> : null}

      {route.name === "coverage" ? <CoverageScreen navigate={go} /> : null}

      {route.name === "notfound" ? (
        <EmptyState
          icon="explore_off"
          title="هذي الصفحة مو موجودة"
          body={route.path}
          action={
            <Button icon="home" onClick={() => go({ name: "catalog" })}>
              رجوع للرئيسية
            </Button>
          }
        />
      ) : null}
    </AppShell>
  );
}
